<template>
    <div>
        <tb-skeleton v-if="!initialized" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

        <div v-else-if="user">
            <div class="row">
                <div class="col-lg-6">
                    <div class="card">
                        <div class="card-header bg-light">
                            <h5 class="card-title">Bild</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="!busy_saving && user.image_url">
                                <img :src="user.image_url" alt="image">
                            </div>
                            <div v-else>Ingen bild</div>
                        </div>
                        <div class="card-footer">
                            <ValidationObserver ref="uploadimageform">
                                <form @submit.prevent="uploadImage">
                                    <div class="row">
                                        <label class="col-2 col-form-label">Ladda upp bild:</label>
                                        <ValidationProvider ref="imageinputprovider" tag="div" class="col-10" vid="file" name="Fil" rules="required|image" v-slot="{ errors }">
                                            <div class="form-group mb-0">
                                                <div class="input-group">
                                                    <input type="file" class="form-control h-auto" @change="imageInputChanged">
                                                    <div class="input-group-append">
                                                        <limitless-button ref="btnuploadimage" btn-class="btn-light" type="button"
                                                                          :disabled="!(upload_images.length > 0) || busy_uploading_image" @click="uploadImage">Ladda upp</limitless-button>
                                                    </div>
                                                </div>
                                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </form>
                            </ValidationObserver>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="card">
                        <div class="card-header bg-light">
                            <h5 class="card-title">Signatur</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="!busy_saving && user.signature_url">
                                <img :src="user.signature_url" alt="signature">
                            </div>
                            <div v-else>Ingen signatur</div>
                        </div>
                        <div class="card-footer">
                            <ValidationObserver ref="uploadsignatureform">
                                <form @submit.prevent="uploadSignature">
                                    <div class="row">
                                        <label class="col-2 col-form-label">Ladda upp signatur:</label>
                                        <ValidationProvider ref="signatureinputprovider" tag="div" class="col-10" vid="file" name="Fil" rules="required|image" v-slot="{ errors }">
                                            <div class="form-group mb-0">
                                                <div class="input-group">
                                                    <input type="file" class="form-control h-auto" @change="signatureInputChanged">
                                                    <div class="input-group-append">
                                                        <limitless-button ref="btnuploadsignature" btn-class="btn-light" type="button"
                                                                          :disabled="!(upload_signatures.length > 0) || busy_uploading_signature" @click="uploadSignature">Ladda upp</limitless-button>
                                                    </div>
                                                </div>
                                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </form>
                            </ValidationObserver>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="not_found">
            <i>Användaren finns inte</i>
        </div>
        <div v-else>
            <i>Något gick fel...</i>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';

export default {
    components: {
        LimitlessButton,
    },
    props: {
        id: {
            type: [String, Number],
            default: '',
        },
    },
    data: () => ({
        initialized: false,
        busy_saving: false,
        not_found: false,
        user: null,
        upload_images: [],
        upload_signatures: [],
        busy_uploading_image: false,
        busy_uploading_signature: false,
    }),
    mounted() {
        this.getUser();
    },
    methods: {
        getUser() {
            apiClient.get('/vue/users/' + this.id + '')
                .then((response) => {
                    this.user = response.data.user;
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                });
        },
        async imageInputChanged(event) {
            const { valid } = await this.$refs.imageinputprovider.validate(event);

            if (valid && event.target.files) {
                // eslint-disable-next-line prefer-destructuring
                this.upload_images = event.target.files;
            } else {
                this.upload_images = [];
            }
        },
        async signatureInputChanged(event) {
            const { valid } = await this.$refs.signatureinputprovider.validate(event);

            if (valid && event.target.files) {
                this.upload_signatures = event.target.files;
            } else {
                this.upload_signatures = [];
            }
        },
        async uploadImage() {
            if (this.upload_images && this.upload_images[0]) {
                this.busy_uploading_image = true;
                let config = {};
                const formData = new FormData();
                formData.append('file', this.upload_images[0]);
                config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                };
                await apiClient.post('/vue/users/' + this.user.UserID + '/upload_image', formData, config)
                    .then(() => {
                        this.$refs.btnuploadimage.triggerSaved();
                        this.upload_images = [];
                        this.getUser();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.uploadimageform.setErrors(parsederror);
                        }
                        this.$refs.btnuploadimage.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_uploading_image = false;
                    });
            }
        },
        async uploadSignature() {
            if (this.upload_signatures && this.upload_signatures[0]) {
                this.busy_uploading_signature = true;
                let config = {};
                const formData = new FormData();
                formData.append('file', this.upload_signatures[0]);
                config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                };
                await apiClient.post('/vue/users/' + this.user.UserID + '/upload_signature', formData, config)
                    .then(() => {
                        this.$refs.btnuploadsignature.triggerSaved();
                        this.upload_signatures = [];
                        this.getUser();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.uploadsignatureform.setErrors(parsederror);
                        }
                        this.$refs.btnuploadsignature.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_uploading_signature = false;
                    });
            }
        },
    },
};

</script>
